import {
  BookOpen, ChartNoAxesColumnIncreasing, ClipboardCheck, Clock3, GraduationCap,
  History, Library, LogOut, Map, Menu, RefreshCw, Route, Target, Users, X,
  Blocks, BriefcaseBusiness, ScanSearch, Workflow,
} from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCapabilities } from "../context/CapabilitiesContext";
import { usePortal } from "../context/PortalContext";
import { formatDate } from "../lib/format";

const navigation = [
  { to: "/overview", label: "概览", icon: ChartNoAxesColumnIncreasing },
  { to: "/learn", label: "开始学习", icon: GraduationCap },
  { to: "/plan", label: "学习规划", icon: Route },
  { to: "/progress", label: "能力进度", icon: Target },
  { to: "/methods", label: "学习方法", icon: Map },
  { to: "/roles", label: "双方职责", icon: Users },
  { to: "/outcomes", label: "达标结果", icon: ClipboardCheck },
  { to: "/resources", label: "教材资料", icon: Library },
  { to: "/history", label: "学习记录", icon: History },
  { to: "/panorama", label: "产品全景", icon: Workflow },
  { to: "/cases", label: "案例工作台", icon: Blocks },
  { to: "/projects", label: "项目驾驶舱", icon: BriefcaseBusiness },
  { to: "/ai-review", label: "AI 审查台", icon: ScanSearch },
];

export function AppLayout() {
  const { tracks, selectedSlug, selectTrack, track, loading, error, refresh } = usePortal();
  const { loginName, logout } = useAuth();
  const capabilities = useCapabilities();
  const [open, setOpen] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  async function signOut() {
    setLogoutError(null);
    try {
      await logout();
    } catch (reason) {
      setLogoutError(reason instanceof Error ? reason.message : String(reason));
    }
  }
  return (
    <div className="app-shell">
      <aside className={`sidebar ${open ? "is-open" : ""}`}>
        <div className="brand-row">
          <NavLink to="/overview" className="brand-link" onClick={() => setOpen(false)}>
            <span className="brand-mark">LP</span>
            <span>学习进程</span>
          </NavLink>
          <button className="icon-button mobile-only" type="button" aria-label="关闭导航" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav className="primary-nav" aria-label="主要导航">
          {navigation.map(({ to, label, icon: Icon }, index) => (
            <NavLink key={to} to={to} onClick={() => setOpen(false)} className={({ isActive }) => `nav-link ${isActive ? "is-active" : ""}`}>
              <span className="nav-index">{String(index + 1).padStart(2, "0")}</span>
              <Icon size={18} strokeWidth={1.8} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-foot">
          <Clock3 size={16} />
          <span>每 15 秒检查进度更新</span>
        </div>
      </aside>

      <div className="workspace">
        <header className="topbar">
          <button className="icon-button mobile-only" type="button" aria-label="打开导航" onClick={() => setOpen(true)}><Menu size={21} /></button>
          <div className="track-control">
            <BookOpen size={17} />
            <select value={selectedSlug} aria-label="选择学习轨道" onChange={(event) => selectTrack(event.target.value)}>
              {tracks.map((item) => <option key={item.slug} value={item.slug}>{item.title}</option>)}
            </select>
          </div>
          <div className="sync-state">
            <span className="sync-dot" aria-hidden="true" />
            <span>{loading ? "同步中" : `已同步 ${formatDate(track?.generated_at)}`}</span>
            <button className="icon-button" type="button" aria-label="立即同步" onClick={() => void refresh()} disabled={loading}><RefreshCw size={17} /></button>
          </div>
        </header>
        {!capabilities.practice.writable && <div className="mode-banner" role="status">当前为只读模式：可浏览学习数据，但不能创建练习或保存阅读进度。</div>}
        {capabilities.practice.mode === "development" && <div className="mode-banner" role="status">当前为开发练习模式：证据仅供调试，不计入正式掌握状态。</div>}
        <div className="account-strip"><span>{loginName}</span><button type="button" onClick={() => void signOut()}><LogOut size={15} />退出</button></div>
        {(error || logoutError) && <div className="error-banner" role="alert">{error || logoutError}</div>}
        <main className="content"><Outlet /></main>
      </div>
      {open && <button className="sidebar-scrim mobile-only" type="button" aria-label="关闭导航" onClick={() => setOpen(false)} />}
    </div>
  );
}
